import _ from 'underscore';
import Backbone from 'backbone';
import ko from 'knockout';
import template from './template.pug';
import ViewModel from './viewmodel';

/**
* Provide languages display & edit functions
*
* <br>It enabled functions:
*   update the selector from display mode to edit modevs,
*   select from the provided languges,
*   update suggested languages,
*   adopt the suggested languages
*
* <br>Triggered event selectedLanguages:changed when user selection changes
*/
export class MultiLanguageSelector extends Backbone.View {
  /**
   * Initilize view settings
   *
   * @param {Object} param - An object containing these default options for view:
   * @param {Object} param.i18nStrings - view customized description settings
   * @param {string} param.i18nStrings.Description - the customized string for view description
   * @param {string} param.i18nStrings.SuggestedLanguagesDescription -
   *   the customized string for suggested languages description
   * @param {Object[]} param.languages - the languages set to choose from, default empty
   * @param {Object[]} param.suggestedLanguages - languages sugguested to choose, default empty
   * @param {boolean} param.inEditMode - whether the selector is in edit mode, default true
   * @param {boolean} param.allLanguageExclusive - whether 'all' language is exclusive
   *   when true, select 'all' will disable all other selections
   *   default true
   * @param {boolean} param.inSingleColumnVersion - whether to one language per row
   *   (by default will be three)
   * @param {Object} param.i18n - the localization settings, by default return the provided key
   *
   * @returns {void} void
  */
  initialize({
    i18nStrings,
    languages,
    suggestedLanguages = [],
    selectedLanguages = [],
    inEditMode = true,
    allLanguageExclusive = true,
    inSingleColumnVersion = false,
    i18n = { getString: key => key },
    activityLogger,
    scenario,
  }) {
    this.options = _.defaults({
      i18nStrings,
      languages,
      suggestedLanguages,
      selectedLanguages,
      inEditMode,
      allLanguageExclusive,
      i18n,
      activityLogger,
      scenario,
    }, {
      i18nStrings: {
        Description: i18n.getString('MultiLanguageSelector_Description'),
        SuggestedLanguagesDescription: i18n.getString('MultiLanguageSelector_SuggestedLanguageDescription'),
      },
    });

    this.inSingleColumnVersion = inSingleColumnVersion;
    this.koModel = new ViewModel(this.options);
    this.koModel.selectedLanguages.subscribe((selectedLangauges) => {
      if (this.koModel.allLanguageExclusive() &&
        this.koModel.allLanguageSelected() &&
        selectedLangauges.length > 1) {
        this.koModel.selectedLanguages([this.koModel.ALL_LANGUAGES]);
      } else {
        this.trigger('selectedLanguages:changed', this.getSelectedLanguages());
      }
    }, this);

    this.logInteraction = _.once(() => {
      if (this.options.activityLogger) {
        this.options.activityLogger.signal(
          'multi language selector language change',
          this.options.scenario
        );
      }
    });
  }

  get events() {
    return {
      'click label.checkbox-inline': 'logInteraction',
    };
  }

  render() {
    this.$el.empty().append(template(_.extend(
      {
        i18n: this.options.i18n,
        SelectedLanguages: this.koModel.selectedLanguagesName().join(','),
        languageOptions: this.koModel.languageOptions,
        inSingleColumnVersion: this.inSingleColumnVersion,
      },
      this.options.i18nStrings
    )));
    ko.applyBindings(this.koModel, this.el);
    return this;
  }

  /**
   * Get suggested languages
   *
   * @returns {Object[]} selected languages
  */
  getSelectedLanguages() {
    return this.koModel.selectedLanguages();
  }

  /**
   * Get suggested languages names
   *
   * @returns {Object[]} selected language names
  */
  getSelectedLanguageNames() {
    return this.koModel.selectedLanguagesName();
  }

  /**
   * Update suggested languages
   *
   * @param {Object[]} languages - new suggested languages
   *
   * @returns {void} void
  */
  updateSuggestedLanguages(languages) {
    this.koModel.updateSuggestedLanguages(languages);
  }
}
